import type { AxiosError } from "axios";

// Custom auth headers are included alongside the standard ones because the repo authenticates several
// partner boundaries with bearer-equivalent secrets in bespoke headers, and an unredacted failure ships
// the plaintext secret to Sentry.
const REDACTED_HEADER_KEYS = new Set([
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-remi-key",
  "x-api-key",
  "x-claim-token",
  "x-sunlight-webhook-token",
]);
const REDACTED_AUTH_KEYS = ["username", "password"] as const;
const REDACTED = "[REDACTED]";

const escapeRegExpLiteral = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Derived from the set above so the wire block can never drift from the header object. Keys are escaped
// because a name is data, not a pattern: unescaped, `(` throws here at module scope so every backend
// package fails to import, and `.`/`+` mismatch the real header, leaving the secret unredacted.
export const buildRedactedHeaderLinePattern = (keys: Iterable<string>): RegExp =>
  new RegExp(`^(${[...keys].map(escapeRegExpLiteral).join("|")}):.*$`, "gim");

const REDACTED_HEADER_LINE_PATTERN = buildRedactedHeaderLinePattern(REDACTED_HEADER_KEYS);

function isStringIndexedObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function redactHeaderObject(headers: unknown): void {
  if (!isStringIndexedObject(headers)) {
    return;
  }
  for (const key of Object.keys(headers)) {
    if (REDACTED_HEADER_KEYS.has(key.toLowerCase())) {
      headers[key] = REDACTED;
    }
  }
}

// `@` is deliberately not excluded from the class, so the match extends to the last `@` in the authority —
// stopping at the first one leaves the tail of a password that contains a literal `@` unredacted.
const URL_USER_INFO_PATTERN = /^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)[^/?#]*@/;

const redactUrlUserInfo = (url: string): string => url.replace(URL_USER_INFO_PATTERN, `$1${REDACTED}@`);

function redactResponseUrl(holder: unknown): void {
  if (!isStringIndexedObject(holder)) {
    return;
  }
  if (typeof holder.responseUrl === "string") {
    holder.responseUrl = redactUrlUserInfo(holder.responseUrl);
  }
}

// follow-redirects keeps its own copies of whatever went into `config.auth`: a "user:pass" string on
// `_options.auth`, and the same pair as URL userinfo on `_currentUrl`. Neither is a header, so the wire
// block below is scrubbed while these two survive.
function redactRedirectableCredentials(redirectable: unknown): void {
  if (!isStringIndexedObject(redirectable)) {
    return;
  }
  if (typeof redirectable._currentUrl === "string") {
    redirectable._currentUrl = redactUrlUserInfo(redirectable._currentUrl);
  }
  if (isStringIndexedObject(redirectable._options)) {
    if (typeof redirectable._options.auth === "string") {
      redirectable._options.auth = REDACTED;
    }
    redactHeaderObject(redirectable._options.headers);
  }
}

// Node's raw ClientRequest keeps the literal wire header block on `_header`, bypassing the header-object redaction above.
function redactRawRequestHeaderBlock(request: unknown): void {
  if (!isStringIndexedObject(request)) {
    return;
  }
  if (typeof request._header === "string") {
    request._header = request._header.replace(
      REDACTED_HEADER_LINE_PATTERN,
      (_match, name: string) => `${name}: ${REDACTED}`,
    );
  }
  redactHeaderObject(request._headers);
  redactResponseUrl(request);
  redactResponseUrl(request.res);
  redactRedirectableCredentials(request._redirectable);
}

// Axios converts `config.auth` into an Authorization header on the wire, so the wire-block redactor scrubs
// that derived copy while the plaintext pair in `config.auth` survives untouched.
function redactBasicAuthCredentials(auth: unknown): void {
  if (!isStringIndexedObject(auth)) {
    return;
  }
  for (const key of REDACTED_AUTH_KEYS) {
    if (key in auth) {
      auth[key] = REDACTED;
    }
  }
}

function redactConfig(config: unknown): void {
  if (!isStringIndexedObject(config)) {
    return;
  }
  redactHeaderObject(config.headers);
  redactBasicAuthCredentials(config.auth);
}

// error.response.config is required by axios's own types, but real-world/partial errors
// (network-layer wrappers, test doubles) don't always match — never trust the type here.
export function redactAxiosError(error: AxiosError): void {
  redactConfig(error.config);
  redactHeaderObject(error.response?.headers);
  redactConfig(error.response?.config);
  redactRawRequestHeaderBlock(error.request);
  redactRawRequestHeaderBlock(error.response?.request);
}
